/**
 * Match-engine client — the ONLY way apps/player talks to the match engine
 * (contracts v0.3 surface, docs D-010/D-011).
 *
 * - Base URL: VITE_MATCH_URL (default http://localhost:8790).
 * - AUTH (D-010): every call carries `Authorization: Bearer <supabase_jwt>` —
 *   the D-009 token off the stored auth session (platform auth.getSession()).
 *   No token ⇒ MatchApiError("not_authenticated") BEFORE any network I/O.
 * - Every request body AND every response body is zod-validated against
 *   @matchday/contracts at the boundary — a drifted server payload throws
 *   MatchApiError("bad_response") at the call site, never lands in the UI.
 * - Server errors (contracts ApiError shape) surface as MatchApiError with
 *   the documented code (`snapped`, `max_hosted`, `bad_link`, …).
 *
 * Path map (verified against services/match-engine/src/service.ts):
 *   GET  /feed?night={code}        → FeedResponse (night CODE, not id)
 *   POST /match/create             → CreateMatchRequest  → CreateMatchResponse
 *   POST /match/claim              → ClaimRequest        → ClaimResponse
 *   POST /match/link-opened        → LinkOpenedRequest   → {match} (ignored)
 *   POST /match/withdraw           → WithdrawRequest     → {match} (ignored)
 *   GET  /match/{id}               → MatchStateResponse
 *   POST /match/result             → SubmitResultRequest → {match: Match}
 *   POST /match/verify             → VerifyResultRequest → {match: Match}
 *   POST /match/chat               → ChatPostRequest     → ChatMessageOut
 *   GET  /match/{id}/chat          → ChatMessageOut[]
 *   GET  /leaderboard?night={code} → LeaderboardResponse (night CODE, not id)
 *
 * CONTRACT-CHANGE REQUESTS FILED (see BUILDER-feed report):
 *   1. No release endpoint for a CLAIMED hosted match (PULLED UP escape hatch)
 *      — withdraw is the documented interim.
 *   2. No self-identity endpoint (GET /me → PlayerIdentity) — the takeover's
 *      HOME card renders "YOU" until one exists.
 *   3. The {match: Match} submit/verify/withdraw/link-opened envelope should
 *      be ratified in packages/contracts/src/api.ts (engine parses it today).
 */
import {
  ApiError,
  ChatMessageOut,
  ChatPostRequest,
  ClaimRequest,
  ClaimResponse,
  CreateMatchRequest,
  CreateMatchResponse,
  FeedResponse,
  LeaderboardResponse,
  LinkOpenedRequest,
  Match,
  MatchStateResponse,
  PlayerIdentity,
  SubmitResultRequest,
  VerifyResultRequest,
  WithdrawRequest,
} from "@matchday/contracts";
import { z } from "zod";
import { auth } from "./platform";

export const MATCH_URL: string =
  (import.meta.env?.VITE_MATCH_URL as string | undefined) ?? "http://localhost:8790";

/** Where the entered night code lives for the session (Landing writes it;
 *  feed/wizard read it — the engine surface is night-scoped). */
export const NIGHT_CODE_KEY = "matchday.night_code";

export class MatchApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
    public readonly retryAfterS?: number,
  ) {
    super(`match api error ${status}: ${code}`);
    this.name = "MatchApiError";
  }
}

/** GET /match/{id}/chat — the ratified {messages} envelope (BREAK-1 reconciled;
 *  contracts v0.4 ChatListResponse — the engine's shape won, the client unwraps). */
const ChatListResponse = z.object({ messages: z.array(ChatMessageOut) });

/** Engine envelope for submit/verify (contract-change request #3). */
const MatchEnvelope = z.object({ match: Match });
export type MatchEnvelope = z.infer<typeof MatchEnvelope>;

export interface MatchApiOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The D-009 bearer source. Default: stored session's supabase_jwt. */
  getToken?: () => Promise<string | null>;
}

export interface MatchApi {
  /** Takes the night CODE (e.g. "BLG-KICK") — the feed is night-scoped. */
  getFeed(nightCode: string): Promise<FeedResponse>;
  createMatch(req: CreateMatchRequest): Promise<CreateMatchResponse>;
  claimMatch(req: ClaimRequest): Promise<ClaimResponse>;
  linkOpened(req: LinkOpenedRequest): Promise<void>;
  withdraw(req: WithdrawRequest): Promise<void>;
  getMatch(matchId: string): Promise<MatchStateResponse>;
  /** POST /match/result → engine {match} envelope → refreshed single truth. */
  submitResult(req: SubmitResultRequest): Promise<MatchStateResponse>;
  /** POST /match/verify → engine {match} envelope → refreshed single truth. */
  verifyResult(req: VerifyResultRequest): Promise<MatchStateResponse>;
  postChat(req: ChatPostRequest): Promise<ChatMessageOut>;
  getChat(matchId: string): Promise<ChatMessageOut[]>;
  /** POST /match/release — the PULLED UP escape (host, CLAIMED only; v0.4). */
  release(matchId: string): Promise<Match>;
  /** GET /me — the caller's own PlayerIdentity (v0.4). */
  getMe(): Promise<PlayerIdentity>;
  /** Takes the night CODE (e.g. "BLG-KICK") — engine resolves it. */
  getLeaderboard(nightCode: string): Promise<LeaderboardResponse>;
}

/** Default token source: the stored auth session's D-009 supabase_jwt. */
async function sessionToken(): Promise<string | null> {
  const session = await auth.getSession();
  return session?.supabase_jwt ?? null;
}

export function createMatchApi(options: MatchApiOptions = {}): MatchApi {
  const base = (options.baseUrl ?? MATCH_URL).replace(/\/$/, "");
  const fetchImpl = options.fetchImpl ?? fetch;
  const getToken = options.getToken ?? sessionToken;

  async function throwApiError(res: Response): Promise<never> {
    let code = "unknown_error";
    let detail: unknown;
    let retryAfterS: number | undefined;
    try {
      const parsed = ApiError.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error;
        detail = parsed.data.detail;
        retryAfterS = parsed.data.retry_after_s;
      }
    } catch {
      // non-JSON error body — the status + unknown_error is all we have
    }
    throw new MatchApiError(res.status, code, detail, retryAfterS);
  }

  async function call<T>(
    method: "GET" | "POST",
    path: string,
    body: unknown,
    // Input typed `unknown` so T infers the OUTPUT (branded) type — branded
    // schemas have unbranded inputs and z.ZodType<T> would otherwise unify
    // T down to the unbranded shape.
    schema: z.ZodType<T, z.ZodTypeDef, unknown> | null,
  ): Promise<T> {
    const token = await getToken();
    if (token === null) throw new MatchApiError(0, "not_authenticated");
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers["content-type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetchImpl(`${base}${path}`, init);
    } catch {
      throw new MatchApiError(0, "network_error");
    }
    if (!res.ok) await throwApiError(res);
    if (schema === null) return undefined as T;
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new MatchApiError(res.status, "bad_response", "response body was not JSON");
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new MatchApiError(res.status, "bad_response", parsed.error.issues);
    }
    return parsed.data;
  }

  const getMatch = (matchId: string) =>
    call("GET", `/match/${encodeURIComponent(matchId)}`, undefined, MatchStateResponse);

  return {
    getFeed: (nightCode) =>
      call("GET", `/feed?night=${encodeURIComponent(nightCode)}`, undefined, FeedResponse),
    createMatch: (req) =>
      call("POST", "/match/create", CreateMatchRequest.parse(req), CreateMatchResponse),
    claimMatch: (req) => call("POST", "/match/claim", ClaimRequest.parse(req), ClaimResponse),
    linkOpened: (req) =>
      call("POST", "/match/link-opened", LinkOpenedRequest.parse(req), null),
    withdraw: (req) => call("POST", "/match/withdraw", WithdrawRequest.parse(req), null),
    getMatch,
    // The engine answers submit/verify with a bare {match} envelope; the
    // Match Centre consumes the FULL MatchStateResponse (its single truth),
    // so both re-fetch it after the write is validated.
    submitResult: async (req) => {
      const { match } = await call(
        "POST",
        "/match/result",
        SubmitResultRequest.parse(req),
        MatchEnvelope,
      );
      return getMatch(match.id);
    },
    verifyResult: async (req) => {
      const { match } = await call(
        "POST",
        "/match/verify",
        VerifyResultRequest.parse(req),
        MatchEnvelope,
      );
      return getMatch(match.id);
    },
    postChat: (req) => call("POST", "/match/chat", ChatPostRequest.parse(req), ChatMessageOut),
    getChat: async (matchId) =>
      (await call("GET", `/match/${encodeURIComponent(matchId)}/chat`, undefined, ChatListResponse))
        .messages,
    release: async (matchId) => {
      const { match } = await call(
        "POST",
        "/match/release",
        { match_id: matchId },
        MatchEnvelope,
      );
      return match;
    },
    getMe: () => call("GET", "/me", undefined, PlayerIdentity),
    getLeaderboard: (nightCode) =>
      call("GET", `/leaderboard?night=${encodeURIComponent(nightCode)}`, undefined, LeaderboardResponse),
  };
}

/** The app-wide instance (Centre routes import this too — one client, one law). */
export const matchApi: MatchApi = createMatchApi();
